import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Mail, Lock, User, Eye, EyeOff, ChevronLeft, Check, Loader2
} from "lucide-react";

// ==================== ANIMATIONS ====================
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
};

// ==================== SOCIAL LOGIN ICONS ====================
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const AppleIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

// ==================== INPUT COMPONENT ====================
const Input = ({ icon: Icon, type = "text", placeholder, value, onChange, name, required = false }) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  
  return (
    <div className="relative group">
      <div className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 group-focus-within:text-pink-500 transition-colors">
        <Icon size={18} />
      </div>
      <input
        type={isPassword && showPassword ? "text" : type}
        name={name}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full pl-12 pr-12 py-4 bg-zinc-900/50 border border-zinc-800 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 transition-all"
        data-testid={`input-${name}`}
      />
      {isPassword && (
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors"
        >
          {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      )}
    </div>
  );
};

// ==================== CHECKBOX COMPONENT ====================
const Checkbox = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-3 cursor-pointer group">
    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${
      checked 
        ? 'bg-pink-500 border-pink-500' 
        : 'border-zinc-600 group-hover:border-zinc-400'
    }`}>
      {checked && <Check size={14} className="text-white" />}
    </div>
    <input 
      type="checkbox" 
      checked={checked} 
      onChange={onChange} 
      className="sr-only"
    />
    <span className="text-sm text-zinc-400 group-hover:text-zinc-300">{label}</span>
  </label>
);

// ==================== SOCIAL BUTTON COMPONENT ====================
const SocialButton = ({ icon: Icon, label, onClick, variant = "default" }) => {
  const variants = {
    default: "bg-zinc-900 border-zinc-800 text-white hover:bg-zinc-800 hover:border-zinc-700",
    google: "bg-white border-white text-zinc-800 hover:bg-zinc-100",
    apple: "bg-black border-zinc-800 text-white hover:bg-zinc-900",
    facebook: "bg-[#1877F2] border-[#1877F2] text-white hover:bg-[#166FE5]"
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full py-3.5 px-4 rounded-xl border font-medium flex items-center justify-center gap-3 transition-all ${variants[variant]}`}
      data-testid={`social-${variant}`}
    >
      <Icon />
      <span>{label}</span>
    </button>
  );
};

// ==================== MAIN AUTH PAGE ====================
const AuthPageNew = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [mode, setMode] = useState(searchParams.get("mode") === "register" ? "register" : "login");
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    fullName: "",
    confirmPassword: ""
  });
  
  useEffect(() => {
    const urlMode = searchParams.get("mode");
    if (urlMode === "register" || urlMode === "login") {
      setMode(urlMode);
    }
  }, [searchParams]);
  
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (mode === "register") {
        if (formData.password !== formData.confirmPassword) {
          toast.error("Les mots de passe ne correspondent pas");
          setLoading(false);
          return;
        }
        
        if (formData.password.length < 8) {
          toast.error("Le mot de passe doit contenir au moins 8 caractères");
          setLoading(false);
          return;
        }
        
        const response = await axios.post(`${API}/auth/register`, {
          email: formData.email,
          password: formData.password,
          full_name: formData.fullName
        });
        
        if (response.data.token) {
          login(response.data.token, response.data.user);
          toast.success("Bienvenue sur RateShare ! 🎉");
          navigate("/dashboard");
        }
      } else {
        const response = await axios.post(`${API}/auth/login`, {
          email: formData.email,
          password: formData.password
        });
        
        if (response.data.token) {
          login(response.data.token, response.data.user);
          toast.success("Content de vous revoir !");
          navigate("/dashboard");
        }
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Une erreur est survenue";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/google/url`);
      if (response.data.auth_url) {
        window.location.href = response.data.auth_url;
      }
    } catch (error) {
      toast.error("Connexion Google non disponible");
    }
  };

  const handleAppleAuth = () => {
    toast.info("Apple Sign-In bientôt disponible");
  };

  const handleFacebookAuth = () => {
    toast.info("Facebook Login bientôt disponible");
  };
  
  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4" style={{ fontFamily: 'Inter, -apple-system, sans-serif' }}>
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-[150px]" />
      </div>

      {/* Main Card */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeIn}
        className="relative w-full max-w-md"
      >
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors mb-6 group"
          data-testid="back-to-home"
        >
          <ChevronLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
          <span>Retour à l'accueil</span>
        </Link>

        {/* Card Container */}
        <div className="bg-zinc-900/60 backdrop-blur-xl border border-zinc-800 rounded-3xl p-8 shadow-2xl">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-header.png" alt="RateShare" className="w-12 h-12" />
              <span className="text-2xl font-bold text-white">RATE<span className="text-amber-400">⭐</span>SHARE</span>
            </Link>
          </div>

          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-white mb-2">
              {mode === "register" ? "Créer un compte" : "Se connecter"}
            </h1>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <Input
                icon={User}
                type="text"
                name="fullName"
                placeholder="Nom complet"
                value={formData.fullName}
                onChange={handleChange}
                required
              />
            )}
            
            <Input
              icon={Mail}
              type="email"
              name="email"
              placeholder="Adresse email"
              value={formData.email}
              onChange={handleChange}
              required
            />
            
            <Input
              icon={Lock}
              type="password"
              name="password"
              placeholder="Mot de passe"
              value={formData.password}
              onChange={handleChange}
              required
            />
            
            {mode === "register" && (
              <Input
                icon={Lock}
                type="password"
                name="confirmPassword"
                placeholder="Confirmer le mot de passe"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
            )}

            {/* Remember Password / Forgot Password */}
            <div className="flex items-center justify-between">
              <Checkbox
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                label="Enregistrer le mot de passe"
              />
              {mode === "login" && (
                <Link to="/forgot-password" className="text-sm text-pink-400 hover:text-pink-300 transition-colors">
                  Mot de passe oublié ?
                </Link>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 px-6 bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:from-pink-600 hover:to-rose-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-pink-500/25"
              data-testid="submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <span>{mode === "register" ? "S'inscrire" : "Se connecter"}</span>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 my-6">
            <div className="flex-1 h-px bg-zinc-800" />
            <span className="text-xs text-zinc-500 uppercase tracking-wider">ou</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>

          {/* Social Login - Small Square Icons */}
          <div className="text-center">
            <p className="text-zinc-400 text-sm mb-4">Inscription plus facile et rapide</p>
            <div className="flex items-center justify-center gap-4">
              {/* Google */}
              <button
                type="button"
                onClick={handleGoogleAuth}
                className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center hover:scale-105 transition-transform shadow-lg"
                data-testid="social-google"
              >
                <GoogleIcon />
              </button>
              
              {/* Apple */}
              <button
                type="button"
                onClick={handleAppleAuth}
                className="w-14 h-14 bg-black border border-zinc-700 rounded-2xl flex items-center justify-center hover:scale-105 transition-transform shadow-lg"
                data-testid="social-apple"
              >
                <AppleIcon />
              </button>
              
              {/* Facebook */}
              <button
                type="button"
                onClick={handleFacebookAuth}
                className="w-14 h-14 bg-[#1877F2] rounded-2xl flex items-center justify-center hover:scale-105 transition-transform shadow-lg"
                data-testid="social-facebook"
              >
                <FacebookIcon />
              </button>
            </div>
          </div>

          {/* Switch Mode */}
          <div className="mt-6 text-center">
            <p className="text-zinc-400 text-sm">
              {mode === "register" ? "Déjà un compte ?" : "Pas encore de compte ?"}
              <button
                type="button"
                onClick={() => setMode(mode === "register" ? "login" : "register")}
                className="ml-2 text-pink-400 hover:text-pink-300 font-medium transition-colors"
              >
                {mode === "register" ? "Se connecter" : "S'inscrire"}
              </button>
            </p>
          </div>

          {/* Terms */}
          {mode === "register" && (
            <p className="mt-6 text-center text-xs text-zinc-500">
              En vous inscrivant, vous acceptez nos{" "}
              <Link to="/terms" className="text-zinc-400 hover:text-white underline">
                Conditions d'utilisation
              </Link>
              {" "}et notre{" "}
              <Link to="/privacy" className="text-zinc-400 hover:text-white underline">
                Politique de confidentialité
              </Link>
            </p>
          )}
        </div>

        {/* Security Badge */}
        <div className="mt-6 flex items-center justify-center gap-2 text-zinc-500 text-xs">
          <Lock size={12} />
          <span>Connexion sécurisée SSL</span>
        </div>
      </motion.div>
    </div>
  );
};

export default AuthPageNew;
